import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { ProjectProvider } from './lib/ProjectContext';
import { isAuthenticated, initializeAuth, canAccessSignal } from './lib/auth';
import { LanguageProvider } from './lib/LanguageContext';
import './App.css';

// Pages
import Login from './pages/auth/Login';
import AcceptInvite from './pages/auth/AcceptInvite';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/Dashboard';
import ContactList from './pages/contacts/ContactList';
import Pipeline from './pages/pipeline/Pipeline';
import Opportunities from './pages/pipeline/Opportunities';
import Sequences from './pages/sequences/Sequences';
import EmailTemplates from './pages/sequences/EmailTemplates';
import SocialCalendar from './pages/social/SocialCalendar';
import TaskBoard from './pages/tasks/TaskBoard';
import Scheduling from './pages/scheduling/Scheduling';
import Reporting from './pages/reporting/Reporting';
import Configuration from './pages/settings/Configuration';
import Companies from './pages/Companies';
import Documents from './pages/documents/Documents';
import DocumentEditorPage from './pages/documents/DocumentEditor';
import ESignature from './pages/documents/ESignature';
import QRCodes from './pages/qrcodes/QRCodes';
import SignPage from './pages/sign/SignPage';
import PromoCampaigns from './pages/promo/PromoCampaigns';
import PromoListen from './pages/promo/PromoListen';
import Interviews from './pages/interview/Interviews';
import InterviewDetail from './pages/interview/InterviewDetail';
import InterviewPublic from './pages/interview/InterviewPublic';
import Yarns from './pages/yarns/Yarns';
import YarnGuest from './pages/yarns/YarnGuest';
import TwineReview from './pages/twine/TwineReview';
import Clients from './pages/clients/Clients';
import Portal, { ClientLogin, ClientInvite, ClientReset } from './pages/portal/Portal';
import Bulletin from './pages/bulletin/Bulletin';
import Signups from './pages/signups/Signups';
import PublicBoard from './pages/board/PublicBoard';
import PublicSignup from './pages/board/PublicSignup';
import MyCard from './pages/cards/MyCard';
import PublicCard from './pages/cards/PublicCard';
import Invoices from './pages/invoices/Invoices';
import InvoicePublic from './pages/invoices/InvoicePublic';
import Expenses from './pages/expenses/Expenses';
import Time from './pages/time/Time';
import Quotes from './pages/quotes/Quotes';
import ProjectHub from './pages/projects/ProjectHub';
import Notes from './pages/notes/Notes';
import QuotePublic from './pages/quotes/QuotePublic';
import { Layout } from './components/layout/Layout';
import { useModules } from './lib/modules';

// A page that belongs to a module the company doesn't have
const ModuleOff = () => (
  <Layout title="Not switched on">
    <div className="max-w-lg mx-auto mt-16 rounded-xl border border-[#E3DDD1] bg-white p-8 text-center">
      <h1 className="text-xl font-semibold text-[#1E2A33] mb-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>This isn't switched on for your company</h1>
      <p className="text-sm text-[#5C6670]">If you'd like to use it, ask Stagehead to turn it on.</p>
    </div>
  </Layout>
);

const ModuleGate = ({ module, children }) => {
  const mods = useModules();
  if (Array.isArray(mods) && !mods.includes(module)) return <ModuleOff />;
  return children;
};

// Protected Route wrapper (module: only for companies with that module switched on)
const ProtectedRoute = ({ children, module }) => {
  const isAuth = isAuthenticated();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuth) {
      navigate('/login', { replace: true });
    }
  }, [isAuth, navigate]);

  if (!isAuth) {
    return null;
  }

  return module ? <ModuleGate module={module}>{children}</ModuleGate> : children;
};

// Permission gate — redirects to dashboard if user lacks a specific permission
const PermissionRoute = ({ children, check }) => {
  const navigate = useNavigate();
  const allowed = check();

  useEffect(() => {
    if (!allowed) {
      navigate('/dashboard', { replace: true });
    }
  }, [allowed, navigate]);

  if (!allowed) return null;
  return children;
};

function App() {
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      await initializeAuth();
      setAuthInitialized(true);
    };
    init();
  }, []);

  if (!authInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <img 
            src="/stagehead-os-mark.svg" 
            alt="Stagehead OS" 
            className="w-20 h-20 mx-auto mb-4 rounded-2xl"
          />
          <p className="text-muted-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {localStorage.getItem('heyday_lang') === 'es' ? 'Cargando Stagehead OS...' : 'Loading Stagehead OS...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <LanguageProvider>
        <ProjectProvider>
          <AppRoutes />
        </ProjectProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/sign/:token" element={<SignPage />} />
        <Route path="/promo/:token" element={<PromoListen />} />
        <Route path="/interview/:token" element={<InterviewPublic />} />
        <Route path="/yarn/:token" element={<YarnGuest />} />
        <Route path="/client" element={<Portal />} />
        <Route path="/board/:slug" element={<PublicBoard />} />
        <Route path="/c/:slug" element={<PublicCard />} />
        <Route path="/board/:slug/signup/:id" element={<PublicSignup />} />
        <Route path="/invoice/:token" element={<InvoicePublic />} />
        <Route path="/quote/:token" element={<QuotePublic />} />
        <Route path="/client/login" element={<ClientLogin />} />
        <Route path="/client/invite/:token" element={<ClientInvite />} />
        <Route path="/client/reset/:token" element={<ClientReset />} />

        <Route
          path="/companies"
          element={
            <ProtectedRoute>
              <Companies />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/contacts"
          element={
            <ProtectedRoute>
              <ContactList />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/pipeline"
          element={
            <ProtectedRoute module="pipeline">
              <Pipeline />
            </ProtectedRoute>
          }
        />

        <Route
          path="/pipeline/opportunities"
          element={
            <ProtectedRoute module="pipeline">
              <Opportunities />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/sequences"
          element={
            <ProtectedRoute module="campaigns">
              <PermissionRoute check={canAccessSignal}>
                <Sequences />
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/email-templates"
          element={
            <ProtectedRoute>
              <PermissionRoute check={canAccessSignal}>
                <EmailTemplates />
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/social"
          element={
            <ProtectedRoute module="social">
              <PermissionRoute check={canAccessSignal}>
                <SocialCalendar />
              </PermissionRoute>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <TaskBoard />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/scheduling"
          element={
            <ProtectedRoute>
              <Scheduling />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/reporting"
          element={
            <ProtectedRoute>
              <Reporting />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/settings/projects"
          element={
            <ProtectedRoute>
              <Configuration initialTab="projects" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/company"
          element={
            <ProtectedRoute>
              <Configuration initialTab="company" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/team"
          element={
            <ProtectedRoute>
              <Configuration initialTab="team" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          }
        />

        <Route path="/documents/:id/edit" element={<ProtectedRoute module="editor"><DocumentEditorPage /></ProtectedRoute>} />

        <Route
          path="/esignature"
          element={
            <ProtectedRoute module="esign">
              <ESignature />
            </ProtectedRoute>
          }
        />

        <Route
          path="/qrcodes"
          element={
            <ProtectedRoute module="qrcodes">
              <QRCodes />
            </ProtectedRoute>
          }
        />

        <Route
          path="/promo"
          element={
            <ProtectedRoute module="promo">
              <PromoCampaigns />
            </ProtectedRoute>
          }
        />

        <Route path="/clients" element={<ProtectedRoute module="clients"><Clients /></ProtectedRoute>} />
        <Route path="/yarns" element={<ProtectedRoute><Yarns /></ProtectedRoute>} />
        <Route path="/yarns/:id" element={<ProtectedRoute><Yarns /></ProtectedRoute>} />
        <Route path="/twine" element={<ProtectedRoute module="twine"><TwineReview /></ProtectedRoute>} />
        <Route path="/my-card" element={<ProtectedRoute module="cards"><MyCard /></ProtectedRoute>} />
        <Route path="/bulletin" element={<ProtectedRoute module="bulletin"><Bulletin /></ProtectedRoute>} />
        <Route path="/signups" element={<ProtectedRoute module="signups"><Signups /></ProtectedRoute>} />
        <Route path="/signups/:id" element={<ProtectedRoute module="signups"><Signups /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute module="invoices"><Invoices /></ProtectedRoute>} />
        <Route path="/invoices/:id" element={<ProtectedRoute module="invoices"><Invoices /></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><ProjectHub /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectHub /></ProtectedRoute>} />
        <Route path="/quotes" element={<ProtectedRoute module="quotes"><Quotes /></ProtectedRoute>} />
        <Route path="/quotes/:id" element={<ProtectedRoute module="quotes"><Quotes /></ProtectedRoute>} />
        <Route path="/time" element={<ProtectedRoute module="time"><Time /></ProtectedRoute>} />
        <Route path="/time/:tab" element={<ProtectedRoute module="time"><Time /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute module="expenses"><Expenses /></ProtectedRoute>} />
        <Route path="/expenses/:tab" element={<ProtectedRoute module="expenses"><Expenses /></ProtectedRoute>} />
        <Route path="/interviews" element={<ProtectedRoute module="interviews"><Interviews /></ProtectedRoute>} />
        <Route path="/interviews/:id" element={<ProtectedRoute module="interviews"><InterviewDetail /></ProtectedRoute>} />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
